import React from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { useAuth } from "../AuthProvider";
import Header from "../Header";
import HeaderLinks from "../HeaderLinks";
import Menu from "../Menu";
import Navigation from "../Navigation";
import Footer from "../Footer";
import ErrorBoundary from "../ErrorBoundary";
import ErrorMessage from "./ErrorMessage";
import SmartAssistPanel, { useSidebarLayout } from "../SmartAssistPanel";
import handleResponse from "../../utils/handleFetchResponse";
import { USER_AUTH_KEY } from "../../utils/constants";
import { useMessageToast } from "../../components/MessageProvider";
import { fetchJsonWithRetry } from "../../utils/batchFetch";

import {
  APP_NAME,
  ENHANCEMENT_REQUESTS,
  HUB_CONNECT,
  SUPPORT_EMAIL,
} from "../../utils/constants";
import { RouteContext } from "../RouteProvider";
import { WindowScroll } from "../WindowEvent";

const Layout = ({ children = null }) => {
  const history = useHistory();
  const location = useLocation();

  const [{ isAuthenticated, user }, { setSelectedClient }] = useAuth();
  const { rootRoute, routes } = React.useContext(RouteContext);
  const { sidebarOffset } = useSidebarLayout();

  const isValidPath = routes.some(
    (route) => !route.isHidden && location.pathname.startsWith(route.path),
  );

  const { selectedClient } = user.preferences;

  const toast = useMessageToast();
  const fetchSources = React.useCallback(() => {
    fetchJsonWithRetry(
      `${process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN}/sources/?client_subscription__client_id=${user.preferences.selectedClient.id}`,
    )
      .then(handleResponse)
      .then((response) => {
        const sourcesByOIdsOrCodes = {};
        for (let s of response.results) {
          if (s.oid) {
            sourcesByOIdsOrCodes[s.oid] = s.name;
          }
          if (s.code) {
            sourcesByOIdsOrCodes[s.code] = s.name;
          }
        }
        user.preferences.sources = sourcesByOIdsOrCodes;
        window.localStorage.setItem(USER_AUTH_KEY, JSON.stringify(user));
      })
      .catch((e) => {
        if (
          "SyntaxError: Unexpected end of JSON input".localeCompare(e) !== 0
        ) {
          toast.error("Something went wrong while fetching the sources");
        }
      });
  }, [user, toast]);

  React.useEffect(() => {
    if (user.preferences.selectedClient != null) {
      fetchSources();
    }
  }, [fetchSources]);

  // Offset to shrink the admin UI when sidebar is open
  const isSmartAssistPage = location.pathname === "/smartassist";
  const layoutStyle =
    sidebarOffset > 0 && !isSmartAssistPage
      ? { marginRight: sidebarOffset, transition: "margin-right 0.2s ease" }
      : undefined;
  const pageStyle = {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  };
  const layoutContainerStyle = {
    ...layoutStyle,
    flex: 1,
    display: "flex",
    flexDirection: "column",
  };
  const mainStyle = { flex: 1 };

  return (
    <div style={pageStyle}>
      <Header
        appName={APP_NAME}
        logoUrl="/"
        onLogoClick={(event) => {
          const { pathname } = event.currentTarget;

          event.preventDefault();
          history.push(pathname);
        }}
      >
        <HeaderLinks>
          {user.clients.length > 1 && (
            <HeaderLinks.Item
              label={selectedClient ? selectedClient.name : "Account"}
            >
              {({ hideMenu }) => (
                <Menu>
                  {user.clients.map((client) => (
                    <Menu.Item
                      key={client.id}
                      render={() => (
                        <button
                          onClick={() => {
                            setSelectedClient(client);
                            hideMenu();
                          }}
                        >
                          {client.name}
                        </button>
                      )}
                      selected={
                        selectedClient && selectedClient.id === client.id
                      }
                    />
                  ))}
                </Menu>
              )}
            </HeaderLinks.Item>
          )}
          {user.clients.length === 1 && (
            <HeaderLinks.Item
              label={selectedClient ? selectedClient.name : "Account"}
            ></HeaderLinks.Item>
          )}
          {isAuthenticated ? (
            <HeaderLinks.Item label={user.username}>
              <Menu>
                <Menu.Item render={() => <Link to="/signout">Sign Out</Link>} />
              </Menu>
            </HeaderLinks.Item>
          ) : (
            <HeaderLinks.Item render={() => <Link to="/">Sign In</Link>} />
          )}
        </HeaderLinks>
      </Header>
      <div style={layoutContainerStyle}>
        {isAuthenticated && rootRoute && (
          <Navigation>
            {rootRoute.routes
              .filter(
                (route) =>
                  !route.isHidden &&
                  route.authorizedRoles.includes(user.role) &&
                  route.envAccess.includes(process.env.REACT_APP_ENV),
              )
              .map((route) => (
                <Navigation.Item
                  key={route.path}
                  active={
                    isValidPath && location.pathname.startsWith(route.path)
                  }
                  label={route.label}
                  render={() => <Link to={route.path}>{route.label}</Link>}
                >
                  {route.routes &&
                    route.routes.some((route) => !route.isHidden) && (
                      <Menu>
                        {route.routes
                          .filter((route) => !route.isHidden)
                          .map((route) => (
                            <Menu.Item
                              key={route.path}
                              render={() => (
                                <Link to={route.path}>{route.label}</Link>
                              )}
                            />
                          ))}
                      </Menu>
                    )}
                </Navigation.Item>
              ))}
          </Navigation>
        )}
        <main style={mainStyle}>
          <ErrorBoundary displayError={<ErrorMessage />}>
            {children}
          </ErrorBoundary>
        </main>
        <SmartAssistPanel />
        <Footer appName={APP_NAME}>
          <Footer.Links heading="Explore" columns="10rem 2">
            <Footer.Link
              render={() => <Link to="/dashboard">Dashboard</Link>}
            />
            {/* <Footer.Link
            render={() => <Link to="/review-queue">Review Queue</Link>}
          />
          <Footer.Link render={() => <Link to="/matching">Matching</Link>} /> */}
            <Footer.Link
              render={() => <Link to="/search-records">Search Records</Link>}
            />
            {/* <Footer.Link
            render={() => <Link to="/match-rates">Match Rates</Link>}
          />
          <Footer.Link
            render={() => <Link to="/monitoring">Monitoring</Link>}
          /> */}
          </Footer.Links>
          <Footer.Links heading="Connect">
            <Footer.Link
              href={HUB_CONNECT}
              label="HubConnect"
              target="_blank"
              rel="noopener noreferrer"
            />
            <Footer.Link
              href={ENHANCEMENT_REQUESTS}
              label="Enhancement Requests"
              target="_blank"
              rel="noopener noreferrer"
            />
          </Footer.Links>
          <Footer.Content heading="Contact Us">
            <p>Can't find what you're looking for? Please email us here:</p>
            <p>
              <a href={`mailto:${SUPPORT_EMAIL}`}>Email Support</a>
            </p>
          </Footer.Content>
        </Footer>
      </div>
    </div>
  );
};

export default Layout;
